import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { EditIcon, TrashIcon } from 'lucide-react';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { DataTable, type Column } from '@/components/custom/data-table';
import { adminsApi } from '@/lib/api/admins';
import type { Admin } from '@/types/entities';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

export function AdminAdminsPage() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<Admin | undefined>();
  const [error, setError] = useState<string | null>(null);

  const fetchAdmins = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminsApi.getAll();
      setAdmins(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admins');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleCreate = () => {
    navigate('/admin/dashboard/admins/create');
  };

  const handleDelete = (admin: Admin) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!adminToDelete) return;

    try {
      await adminsApi.delete(adminToDelete.id);
      setDeleteDialogOpen(false);
      setAdminToDelete(undefined);
      await fetchAdmins();
      toast.success('Admin deleted successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete admin';
      setError(message);
      toast.error(message);
    }
  };

  const columns: Column<Admin>[] = [
    {
      key: 'id',
      header: 'ID',
      cell: (admin) => <span className="text-muted-foreground">#{admin.id}</span>,
      className: 'w-20',
      sortable: true,
      sortValue: (admin) => admin.id,
    },
    {
      key: 'email',
      header: 'Email',
      cell: (admin) => admin.email,
      sortable: true,
      sortValue: (admin) => admin.email,
    },
    {
      key: 'display_name',
      header: 'Display Name',
      cell: (admin) => admin.display_name,
      sortable: true,
      sortValue: (admin) => admin.display_name,
    },
    {
      key: 'created_at',
      header: 'Created',
      cell: (admin) => new Date(admin.created_at).toLocaleDateString(),
      className: 'w-32',
      sortable: true,
      sortValue: (admin) => new Date(admin.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      cell: (admin) => (
        <div className="flex items-center gap-2">
          <Link to={`/admin/dashboard/admins/${admin.id}/edit`}>
            <EditIcon className="size-4" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDelete(admin)}
            className="h-8 w-8 text-destructive hover:text-destructive"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ),
      className: 'w-24',
    },
  ];

  return (
    <>
      <PageHeader
        title="Admins"
        description="Manage admin users"
      />

      <PageSection>
        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-destructive">
            {error}
          </div>
        )}

        <DataTable
          data={admins}
          columns={columns}
          searchKeys={['email', 'display_name']}
          searchPlaceholder="Search by email or name..."
          onCreate={handleCreate}
          createLabel="Add Admin"
          emptyMessage="No admins found"
          isLoading={isLoading}
        />
      </PageSection>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Admin"
        description={`Are you sure you want to delete "${adminToDelete?.email}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />
    </>
  );
}
